import React from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Tooltip from '@mui/material/Tooltip'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import InputData from '@/javascript/presentation/view-models/input-data'

type Props = Partial<React.ComponentProps<typeof Autocomplete>> & {
  label: string
  value: InputData[]
  setValue: React.Dispatch<React.SetStateAction<InputData[]>>
  fetchOptions: (searchText?: string) => Promise<InputData[]>
  optionsProps?: {
      tooltipTitle: string
      disable?: (option: any) => boolean
    }
}

const AutocompleteFetchInput: React.FC<Props> = ({
  label, value, setValue, fetchOptions, disabled, optionsProps, ...rest
}) => {
  const [options, setOptions] = React.useState<InputData[]>([])
  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null)

  const handleOpen = React.useCallback(async () => {
    setOpen(true);
    if (options.length > 0 && !search) return
    setLoading(true)
    setSearch('')
    try {
      const options = await fetchOptions()
      setOptions(options)
    } catch (e) {
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [search, options.length])

  const selectedCountText = (): string => {
    switch (true) {
      case disabled: return '-'
      case value.length === 1: return value[0].customLabel || value[0].label
      case value.length > 1: return `${value.length} selecionados`
      default: return 'Todos'
    }
  }

  return (
    <Autocomplete
      sx={{
        width: '20rem', "& .MuiOutlinedInput-root": { paddingRight: '0px !important' },
        "& .MuiButtonBase-root": { color: 'action.active' },
        "& .MuiInputAdornment-root": {
          maxWidth: '50%',
          "& .MuiTypography-root": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
        },
        "& .MuiInputBase-root": { display: 'flex', flexWrap: 'nowrap', "& .MuiInputBase-input": { flex: 0.6 } }
      }}
      disabled={disabled}
      multiple
      disableCloseOnSelect
      options={options}
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      loading={loading}
      getOptionLabel={(option: InputData) => option.label}
      onChange={(_event, newValue: InputData[]) => setValue(newValue)}
      value={value}
      isOptionEqualToValue={(option: InputData, value: InputData) => option.value === value.value}
      filterOptions={(x) => x}
      renderValue={(_selected) => null}
      onInputChange={(_event, value, reason) => {
        if (reason === 'input') {
          if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
          debounceTimeout.current = setTimeout(async () => {
            try {
              setOptions([])
              setSearch(value)
              setLoading(true)
              await new Promise(resolve => setTimeout(resolve, 1000))
              const options = await fetchOptions(value)
              setOptions(options)
            } catch (e) {
              setOptions([])
            } finally {
              setLoading(false)
            }
          }, 500)
        }
      }}
      renderOption={(props, option: any, { selected }) => {
        return (
          <Tooltip key={option.value} title={optionsProps?.tooltipTitle} placement="right" arrow
            disableHoverListener={!optionsProps?.tooltipTitle || !optionsProps?.disable?.(option)}
            slotProps={{
              popper: {
                modifiers: [{ name: 'offset', options: { offset: [0, -30] } }]
              }
            }}
          >
            <span>
              <li {...props} key={option.value}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <Typography sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {option.label}
                </Typography>
              </li>
            </span>
          </Tooltip>
        )
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="end">{selectedCountText()}</InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} sx={{ mr: 2 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )
      }}
      {...rest}
    />
  )
}

export default AutocompleteFetchInput
